<template>
<div class="mapbox-container">
  <div class="mapbox-wrapper">
    <div ref="mapContainer" class="map"></div>
    <div v-if="showCountryImages" class="map-dark-overlay"></div>
    <div v-if="showCountryImages" class="country-images-overlay">
			<div v-for="country in countries" :key="country.name" class="country-image-wrapper">
				<div class="title_country"> {{country.displayName}}</div>
				<div class="tick_country">
					<svg viewBox="0 0 52 52" class="tick-svg">
						<circle class="tick-circle" cx="26" cy="26" r="25" fill="white"/>
						<path class="tick-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
					</svg>
				</div>
				<img :src="country.image" :alt="country.fr" :style="country.style" class="country-image">
			</div>
    </div>
  </div>
</div>
</template>

<script>
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import InvestmentResource from '@/common/resources/investment'
import CountryData from '@/common/components/MyPatrimonialLandscape/data/Country'

export default {
	name: 'MapboxComponent',
	props: {
		accessToken: {
			type: String,
			required: true
		},
		center: {
			type: Array,
			default: () => [2.3522, 48.8566] // Paris par d�faut
		},
		zoom: {
			type: Number,
			default: 12
		},
		styleProp: {
			type: String,
			default: 'mapbox://styles/mapbox/satellite-v9'
		},
		markers: {
			type: Array,
			default: () => []
		},
		clientId: {
			type: Number,
			default: null
		}
	},
	data () {
		return {
			map: null,
			mapMarkers: [],
			showCountryImages: false,
			countries: [],
		}
	},
	mounted () {
		this.loadClientCountries()
		this.initializeMap()
	},
	beforeDestroy () {
		if (this.map) {
			this.map.remove()
		}
	},
	watch: {
		markers: {
			handler (newMarkers) {
				this.updateMarkers(newMarkers)
			},
			deep: true
		},
		clientId: {
			handler (newClientId) {
				if (newClientId) {
					this.loadClientCountries()
				}
			}
		}
	},
	methods: {
		async loadClientCountries () {
			try {
				if (!this.clientId) {
					return
				}

				const response = await InvestmentResource.getCountriesInvestmentProducts(this.clientId)
				if (response.data && response.data.countries) {
					const iso2Codes = response.data.countries
					this.countries = this.buildCountriesFromISO2(iso2Codes)
				}
			} catch (error) {
				console.error('Erreur lors du chargement des pays du client:', error)
			}
		},
		buildCountriesFromISO2 (iso2Codes) {
			const countries = []

			iso2Codes.forEach(iso2Code => {
				// Trouver le pays correspondant dans CountryData
				const country = CountryData.find(c => c.ISO2 === iso2Code)
				const svgFileName = country.type.toLowerCase()
				if (country && svgFileName) {
					try {
						countries.push({
							iso2: iso2Code,
							name: country.type,
							displayName: country.fr,
							image: require(`@/common/assets/img/countries/${iso2Code}.svg`)
						})
					} catch (error) {
						console.warn(`Image SVG non trouvée pour ${country.type}: ${iso2Code}.svg`, error)
					}
				}
			})

			return countries
		},
		initializeMap () {
			mapboxgl.accessToken = this.accessToken

			this.map = new mapboxgl.Map({
				container: this.$refs.mapContainer,
				style: this.styleProp,
				zoom: 1,
				projection:'equirectangular',
				attributionControl: false,
				interactive: false
			})
			// Ajouter les marqueurs initiaux
			this.map.on('load', () => {
				// Forcer le recalcul des dimensions de la carte
				this.map.resize()
				// Charger les images SVG des pays
				this.loadCountryImages()

				// Ajouter une couche pour simuler l'éclairage du soleil
				this.addSunLightLayer()
				// this.updateMarkers(this.markers)
				this.$emit('map-loaded', this.map)
			})
		},
		loadCountryImages () {
			// Afficher les images SVG des pays après 1 seconde
			setTimeout(() => {
				this.showCountryImages = true
			}, 1000)
		},
		addSunLightLayer () {
			// Calculer la position du soleil en fonction de l'heure actuelle
			const now = new Date()
			const hours = now.getUTCHours()
			const minutes = now.getUTCMinutes()

			// Longitude du soleil (15 degrés par heure, 0° à midi UTC)
			const sunLongitude = ((hours - 12) * 15 + (minutes / 60) * 15)

			// Ajouter une source pour l'éclairage
			this.map.addSource('sun-light', {
				type: 'geojson',
				data: {
					type: 'Feature',
					geometry: {
						type: 'Point',
						coordinates: [sunLongitude, 0]
					}
				}
			})

			// Ajouter une couche de heatmap pour simuler la lumière du soleil
			this.map.addLayer({
				id: 'sun-glow',
				type: 'circle',
				source: 'sun-light',
				paint: {
					'circle-radius': {
						stops: [
							[0, 0],
							[20, 800]
						],
						base: 2
					},
					'circle-color': 'rgba(255, 220, 100, 0.1)',
					'circle-blur': 1,
					'circle-opacity': 0.3
				}
			})

			// Ajouter un layer pour les zones d'ombre (nuit)
			this.map.addLayer({
				id: 'night-overlay',
				type: 'background',
				paint: {
					'background-color': 'rgba(0, 0, 0, 0.4)',
					'background-opacity': 0.5
				}
			})

			// Mettre à jour la position du soleil toutes les minutes
			this.sunInterval = setInterval(() => {
				const currentTime = new Date()
				const h = currentTime.getUTCHours()
				const m = currentTime.getUTCMinutes()
				const newSunLongitude = ((h - 12) * 15 + (m / 60) * 15)

				const source = this.map.getSource('sun-light')
				if (source) {
					source.setData({
						type: 'Feature',
						geometry: {
							type: 'Point',
							coordinates: [newSunLongitude, 0]
						}
					})
				}
			}, 60000) // Mise à jour toutes les minutes
		},
		flyTo (center, zoom = this.zoom) {
			if (this.map) {
				this.map.flyTo({
					center: center,
					zoom: zoom
				})
			}
		},
		addMarker (lng, lat, popup = null) {
			const marker = new mapboxgl.Marker()
				.setLngLat([lng, lat])

			if (popup) {
				const popupInstance = new mapboxgl.Popup({ offset: 25 })
					.setHTML(popup)
				marker.setPopup(popupInstance)
			}

			marker.addTo(this.map)
			this.mapMarkers.push(marker)
			return marker
		}
	}
}
</script>

<style>
/* Variables CSS */
:root {
  --tick-color: #4CAF50;
  --beam-color-r: 255;
  --beam-color-g: 64;
  --beam-color-b: 82;
  --particle-color-r: 255;
  --particle-color-g: 215;
  --particle-color-b: 0;
}

/* Container principal */
.mapbox-container {
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.mapbox-container .title {
  text-align: center;
}

.mapbox-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Carte Mapbox */
.map {
  width: 100%;
  height: 100%;
  min-height: 350px;
}

.map .mapboxgl-canvas {
  width: 100% !important;
  height: 100% !important;
}

.mapboxgl-ctrl-bottom-left {
  display: none;
}

/* Overlays */
.map-dark-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 5;
  pointer-events: none;
  animation: darkenMap 1s ease-in 0.01s forwards;
}

.country-images-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  display: flex;
	flex-wrap: wrap;
  justify-content: space-around;
  align-items: center;
  pointer-events: none;
}

/* Country elements */
.country-image-wrapper {
  position: relative;
	max-width: 25%;
	width: 100%;
}

.country-image {
  position: relative;
	display: block;
  width: 95px;
  height: 95px;
	margin: auto;
  object-fit: contain;
  opacity: 0;
  animation: fadeIn 0.5s ease-in-out forwards;
}

.title_country {
  color: white;
  font-size: 0.85rem;
  text-align: center;
  opacity: 0;
  animation: fadeIn 0.5s ease-in-out forwards;
}

/* Tick animation */
.tick_country {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 999;
  width: 20px;
  height: 20px;
  opacity: 0;
  transform: translate(-50%, -50%);
  animation: fadeIn 0.3s ease-in-out forwards;
}

.tick-svg {
  width: 100%;
  height: 100%;
}

.tick-circle {
  fill: transparent;
  stroke: var(--tick-color);
  stroke-width: 2;
  stroke-dasharray: 166;
  stroke-dashoffset: 166;
  animation: tickCircleDraw 0.6s ease-out forwards, tickCircleFill 0.1s 0.6s ease-out forwards;
}

.tick-check {
  fill: none;
  stroke: var(--tick-color);
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 48;
  stroke-dashoffset: 48;
  animation: tickCheckDraw 0.3s 0.6s ease-out forwards;
}

/* Animation delays par wrapper */
.country-image-wrapper:nth-child(1) .country-image,
.country-image-wrapper:nth-child(1) .title_country { animation-delay: 0s; }
.country-image-wrapper:nth-child(1) .tick-circle { animation-delay: 0.5s; }
.country-image-wrapper:nth-child(1) .tick-check { animation-delay: 1.1s; }

.country-image-wrapper:nth-child(2) .country-image,
.country-image-wrapper:nth-child(2) .title_country { animation-delay: 0.5s; }
.country-image-wrapper:nth-child(2) .tick-circle { animation-delay: 1s; }
.country-image-wrapper:nth-child(2) .tick-check { animation-delay: 1.6s; }

.country-image-wrapper:nth-child(3) .country-image,
.country-image-wrapper:nth-child(3) .title_country { animation-delay: 1s; }
.country-image-wrapper:nth-child(3) .tick-circle { animation-delay: 1.5s; }
.country-image-wrapper:nth-child(3) .tick-check { animation-delay: 2.1s; }

.country-image-wrapper:nth-child(4) .country-image,
.country-image-wrapper:nth-child(4) .title_country { animation-delay: 1.5s; }
.country-image-wrapper:nth-child(4) .tick-circle { animation-delay: 2s; }
.country-image-wrapper:nth-child(4) .tick-check { animation-delay: 2.6s; }

.country-image-wrapper:nth-child(5) .country-image,
.country-image-wrapper:nth-child(5) .title_country { animation-delay: 2s; }
.country-image-wrapper:nth-child(5) .tick-circle { animation-delay: 2.5s; }
.country-image-wrapper:nth-child(5) .tick-check { animation-delay: 3.1s; }

.country-image-wrapper:nth-child(6) .country-image,
.country-image-wrapper:nth-child(6) .title_country { animation-delay: 2.5s; }
.country-image-wrapper:nth-child(6) .tick-circle { animation-delay: 3s; }
.country-image-wrapper:nth-child(6) .tick-check { animation-delay: 3.6s; }

.country-image-wrapper:nth-child(7) .country-image,
.country-image-wrapper:nth-child(7) .title_country { animation-delay: 3s; }
.country-image-wrapper:nth-child(7) .tick-circle { animation-delay: 3.5s; }
.country-image-wrapper:nth-child(7) .tick-check { animation-delay: 4.1s; }

.country-image-wrapper:nth-child(8) .country-image,
.country-image-wrapper:nth-child(8) .title_country { animation-delay: 3.5s; }
.country-image-wrapper:nth-child(8) .tick-circle { animation-delay: 4s; }
.country-image-wrapper:nth-child(8) .tick-check { animation-delay: 4.6s; }

/* Light beam marker */
.light-beam-marker {
  position: relative;
  width: 20px;
  height: 100px;
  cursor: pointer;
  transform-style: preserve-3d;
  perspective: 1000px;
}

.light-beam-marker .beam,
.light-beam-marker .pulse,
.light-beam-marker .glow,
.light-beam-marker .particles {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
}

.light-beam-marker .beam {
  width: 4px;
  height: 100%;
  background: linear-gradient(to top, rgba(255, 64, 82, 0.9), rgba(255, 64, 82, 0.6), transparent);
  box-shadow: 0 0 20px rgba(255, 64, 82, 0.8), 0 0 40px rgba(255, 64, 82, 0.4);
  animation: beamPulse 2s ease-in-out infinite;
}

.light-beam-marker .pulse {
  width: 10px;
  height: 10px;
  background: radial-gradient(circle, rgba(255, 64, 82, 1), transparent 70%);
  border-radius: 50%;
  animation: pulseEffect 2s ease-in-out infinite;
}

.light-beam-marker .glow {
  bottom: -3px;
  width: 16px;
  height: 16px;
  background: radial-gradient(circle, rgba(255, 64, 82, 1), transparent 70%);
  border-radius: 50%;
  animation: glowEffect 2s ease-in-out infinite;
}

.light-beam-marker .particles {
  width: 4px;
  height: 100%;
  pointer-events: none;
}

.light-beam-marker .particle {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 2px;
  height: 2px;
  background: rgba(255, 215, 0, 0.9);
  border-radius: 50%;
  box-shadow: 0 0 8px rgba(255, 215, 0, 0.8);
  animation: particleRise 3s linear infinite;
}

.light-beam-marker .particle:nth-child(1) { animation-duration: 2.5s; animation-delay: 0s; }
.light-beam-marker .particle:nth-child(2) { animation-duration: 3s; animation-delay: 0.5s; left: 48%; }
.light-beam-marker .particle:nth-child(3) { animation-duration: 2.8s; animation-delay: 1s; left: 52%; }
.light-beam-marker .particle:nth-child(4) { animation-duration: 3.2s; animation-delay: 1.5s; }
.light-beam-marker .particle:nth-child(5) { animation-duration: 2.6s; animation-delay: 2s; left: 51%; }

/* Animations */
@keyframes fadeIn {
  from { opacity: 0;  }
  to { opacity: 1;  }
}

@keyframes tickCircleDraw {
  to { stroke-dashoffset: 0; }
}

@keyframes tickCircleFill {
  from { fill: transparent; }
  to { fill: white; }
}

@keyframes tickCheckDraw {
  to { stroke-dashoffset: 0; }
}

@keyframes darkenMap {
  from { background-color: rgba(0, 0, 0, 0); }
  to { background-color: rgba(0, 0, 0, 0.7); }
}

@keyframes beamPulse {
  0%, 100% { opacity: 0.8; height: 100%; }
  50% { opacity: 1; height: 120%; }
}

@keyframes pulseEffect {
  0%, 100% { transform: translateX(-50%) scale(1); opacity: 1; }
  50% { transform: translateX(-50%) scale(1.5); opacity: 0.8; }
}

@keyframes glowEffect {
  0%, 100% { transform: translateX(-50%) scale(1); opacity: 0.5; }
  50% { transform: translateX(-50%) scale(2); opacity: 0.2; }
}

@keyframes particleRise {
  0% { bottom: 0; opacity: 0; transform: translateX(-50%) scale(0.5); }
  10% { opacity: 1; transform: translateX(-50%) scale(1); }
  90% { opacity: 0.8; }
  100% { bottom: 100%; opacity: 0; transform: translateX(-50%) scale(0.3); }
}
</style>



// WEBPACK FOOTER //
// mapbox.vue?2185782d